<template>
  <div>
    <div style="margin: auto;" class="pt-3 fit-content">
      <date-picker :selected-date="selectedDate" @onDateUpdate="d => selectedDate = d" />
    </div>
    <template v-if="mappedProviders.length > 0">
      <rating-modal
        :dialog="ratingDialog"
        :date="selectedDate"
        :provider-title="mappedProviders.find(p => p.id === ratingProviderId) ? `(${mappedProviders.find(p => p.id === ratingProviderId).name})` : ''"
        :rated-dishes="ratedDishes"
        @closeRating="closeRating"
        @saveRating="saveRating"
      />
      <v-flex
        v-for="(provider, index) in mappedProviders"
        :key="index"
        xs12
        lg10
        xl8
        class="mb-3 px-2 orders-list-table"
        style="margin:auto;"
      >
        <v-card>
          <v-card-title primary-title>
            <h3 :class="`headline ${provider.color}--text`">
              <span>
                {{ provider.name }}
                <v-tooltip v-if="showTooltip(provider)" right>
                  <v-icon slot="activator" class="pl-2" color="info" style="font-size: 1em;">info</v-icon>
                  <span v-t="'dishRatingsContainer.tooltip'" />
                </v-tooltip>
              </span>
            </h3>
            <v-spacer />
            <v-tooltip bottom :disabled="!disableRatingButton(provider.id)">
              <div slot="activator">
                <v-btn
                  color="primary"
                  class="mx-0 my-0"
                  :disabled="disableRatingButton(provider.id)"
                  @click="ratingProviderId = provider.id; ratingDialog = true"
                >
                  {{ $t('mainComponent.rateLunch') }}
                </v-btn>
              </div>
              <span>{{ ratingButtonTooltip(provider.id) }}</span>
            </v-tooltip>
          </v-card-title>
          <v-data-table
            :no-data-text="$t('dataTables.noData')"
            :headers="headers"
            :items="provider.dishes"
            :pagination.sync="pagination"
            :loading="loading > 0"
            class="elevation-1"
            item-key="title"
            :rows-per-page-items="rowsPerPage"
          >
            <template slot="items" slot-scope="props">
              <tr @click="props.expanded = !props.expanded">
                <td style="min-width: 190px;">{{ props.item.title }}</td>
                <td class="text-xs-right">{{ props.item.avgRating ? props.item.avgRating.toFixed(2) : '-' }}</td>
                <td class="text-xs-right">{{ props.item.ratings.length }}</td>
              </tr>
            </template>
            <template v-if="provider.rating" slot="footer">
              <td colspan="100%" style="text-align: right;" class="pr-4">
                <strong>{{ $t('dishRatingsContainer.averageRating', { rating: provider.rating.toFixed(2) }) }}</strong>
              </td>
            </template>
            <template slot="expand" slot-scope="props">
              <template v-if="props.item.comments.length > 0">
                <v-card v-for="(comment, index2) in props.item.comments" :key="index2" flat>
                  <v-card-text style="padding: 4px 16px">
                    <span>• {{ comment }}</span>
                  </v-card-text>
                </v-card>
              </template>
              <v-card v-else>
                <v-card-text style="padding: 4px 16px">
                  <span v-t="'dishRatingsContainer.noComments'" />
                </v-card-text>
              </v-card>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </template>
    <div v-else-if="loading === 0" :style="{color: 'rgba(0,0,0,.54)', display: 'flex', 'flex-direction': 'column', margin: 'auto', 'align-items': 'center'}">
      <v-icon :style="{'font-size': '13rem'}">star_half</v-icon>
      <br>
      <div v-t="'dishRatingsContainer.nothingFound'" :style="{'font-size': '3rem'}" />
      <div v-t="'dishRatingsContainer.nothingFoundBody'" :style="{'font-size': '1.5rem'}" />
    </div>
  </div>
</template>

<script>
import { apolloClient } from '@/apollo';
import gql from 'graphql-tag';
import moment from 'moment';
import NotificationService from '@/services/NotificationService';
import ErrorHandler from '@/services/ErrorHandler';
import flattenDeep from 'lodash/flattenDeep';
import { mapGetters, mapState, mapActions } from 'vuex';
import DatePicker from '../common/date-picker/DatePicker.vue';
import RatingModalVue from '../common/rating-modal/RatingModal.vue';

export default {
  name: 'DishRatingsContainer',
  components: {
    'date-picker': DatePicker,
    'rating-modal': RatingModalVue,
  },
  data() {
    return {
      loading: 0,
      ratingDialog: false,
      ratingProviderId: 0,
      ratedDishes: [],
      pagination: {
        sortBy: 'avgRating',
        descending: true,
      },
      day: {
        isFreeOfCharge: true,
        providers: [{
          name: 'Loading...',
          categories: [],
        }],
      },
      dishesToRate: {
        dishes: [],
      },
      selectedDate: this.$route.params.date,
      headers: [
        {
          text: this.$t('dishRatingsContainer.dishName'), align: 'left', sortable: true, value: 'title',
        },
        {
          text: this.$t('dishRatingsContainer.avgRating'), value: 'avgRating', align: 'right', sortable: true,
        },
        {
          text: this.$t('dishRatingsContainer.ratingsCount'), value: 'ratings.length', align: 'right', sortable: true,
        },
      ],
      rateableDays: [moment().format('YYYY-MM-DD')],
    };
  },
  computed: {
    ...mapState('defaultValuesStore', ['dailyLunchStartHour', 'fridayLunchStartHour']),
    ...mapGetters('drawerStore', ['isDrawerOpen']),
    mappedProviders() {
      if (!this.day) {
        return [];
      }

      const result = this.day.providers.map((provider) => {
        const mappedDishes = this.mergeRepeatingDishes(flattenDeep(provider.categories.map(c => c.selections.map(s => s.dishes
          .map(d => ({
            title: d.title,
            comments: d.ratings.map(r => r.comment).filter(comment => comment),
            ratings: d.ratings.map(r => r.rating),
          }))
          .filter(d => d.ratings.length > 0)))));

        return {
          id: provider.id,
          name: provider.name,
          color: provider.color,
          dishes: mappedDishes,
          rating: this.countProviderRating(mappedDishes),
        };
      });

      return result;
    },
    rowsPerPage() {
      return [10, 20, 50, { text: this.$t('dataTables.all'), value: -1 }];
    },
  },
  watch: {
    selectedDate(newValue) {
      this.$router.push({ params: { date: newValue } });
    },
    ratingProviderId(newValue) {
      this.ratedDishes = this.dishesToRate.dishes.filter(d => d.providerId === newValue && !d.alreadyRated).map(d => ({
        id: d.id,
        rating: null,
        comment: '',
        title: d.title,
        isSoup: d.isSoup,
      }));
    },
  },
  async mounted() {
    await this.loadDefaultValues();
  },
  methods: {
    ...mapActions('defaultValuesStore', ['loadDefaultValues']),
    mergeRepeatingDishes(dishes) {
      const result = dishes.reduce((acc, val) => {
        const index = acc.findIndex(a => a.title === val.title);
        if (index !== -1) {
          acc[index] = {
            title: acc[index].title,
            comments: [...acc[index].comments, ...val.comments],
            ratings: [...acc[index].ratings, ...val.ratings],
          };
        } else {
          acc.push(val);
        }

        return acc;
      }, []);

      return result.map(r => Object.assign({}, r, { avgRating: r.ratings.reduce((acc, re) => acc + re, 0) / r.ratings.length }));
    },
    showTooltip(provider) {
      return provider.dishes.length > 0;
    },
    countProviderRating(dishes) {
      let totalRatings = 0;
      const result = dishes.reduce((acc, d) => {
        totalRatings += d.ratings.length;
        return acc + (d.avgRating * d.ratings.length);
      }, 0);

      return result / totalRatings;
    },
    async saveRating() {
      await apolloClient.mutate({
        mutation: gql`mutation SaveDishesRating($data: [DishRatingInput]!){
          rateDishes(data: $data)
        }`,
        variables: {
          data: this.ratedDishes.map(d => ({
            dishId: d.id,
            rating: d.rating,
            comment: d.comment,
          })),
        },
      });
      apolloClient.queryManager.refetchQueryByName('DishesToRateQuery');
      await this.$apollo.queries.dishesToRate.refresh();
      await this.$apollo.queries.day.refresh();
      this.closeRating();
    },
    closeRating() {
      this.ratingDialog = false;
      this.ratingProviderId = 0;
    },
    disableRatingButton(providerId) {
      const lunchTime = this.day.isFreeOfCharge ? this.fridayLunchStartHour : this.dailyLunchStartHour;
      const date = moment(this.dishesToRate.date);
      const today = moment();

      const lunchHasntHappened = today.diff(date, 'days') < 1 && today.get('hours') < lunchTime;
      return lunchHasntHappened || !this.dishesToRate.dishes.some(d => d.providerId === providerId && !d.alreadyRated);
    },
    ratingButtonTooltip(providerId) {
      if (!this.dishesToRate.dishes.some(d => d.providerId === providerId)) {
        return this.$t('dishRatingsContainer.ratingBtnNothingOrdered');
      }
      if (this.dishesToRate.dishes.filter(d => d.providerId === providerId && !d.alreadyRated).length === 0) {
        return this.$t('dishRatingsContainer.ratingBtnAlreadyRated');
      }

      return this.$t('dishRatingsContainer.ratingBtnLunchNotHappenedYet');
    },
  },
  apollo: {
    $loadingKey: 'loading',
    day: {
      fetchPolicy: 'network-only',
      query: gql`query DishRatingsQuery($date: TimelessDate!) {
        day(date: $date) {
          id
          isFreeOfCharge
          providers {
            id
            name
            color
            categories {
              selections {
                dishes {
                  title
                  ratings {
                    rating
                    comment
                  }
                }
              }
            }
          }
        }
      }`,
      variables() {
        return {
          date: this.selectedDate,
        };
      },
      skip() {
        return !this.selectedDate;
      },
      error(error) {
        this.day = null;
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();
        }
      },
    },
    dishesToRate: {
      fetchPolicy: 'network-only',
      query: gql`query DishesToRatePerProviderQuery($date: TimelessDate) {
        dishesToRate(date: $date) {
          date
          dishes {
            id
            isSoup
            title
            providerId
            alreadyRated
          }
        }
      }`,
      variables() {
        return {
          date: this.selectedDate,
        };
      },
      skip() {
        return !this.selectedDate;
      },
      error(error) {
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();
        }
      },
    },
  },
};
</script>
